import React, { memo } from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

import posterImage from '../images/poster.png';
import defaultFaviconImage from '../images/default-favicon.png';

const Head = ({
  title,
  lang = 'en',
  locale = 'en_US',
  description,
  type = 'website',
  typeMeta = [],
  image,
  imageMeta = [],
  meta = [],
  canonical
}) => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          siteUrl
          twitterHandle
        }
      }
    }
  `);

  const ensureTrailingSlash = (url) => {
    return url.endsWith('/') ? url : `${url}/`;
  };

  const defaultTitle = site.siteMetadata.title;
  const metaDescription = description || site.siteMetadata.description;
  const metaTitle = title ? `${title} / ${defaultTitle}` : defaultTitle;
  const metaPoster = site.siteMetadata.siteUrl + (image || posterImage);

  const defaultFavicon = defaultFaviconImage;
  const linkCanonical = canonical
    ? `${site.siteMetadata.siteUrl}${canonical}`
    : site.siteMetadata.siteUrl;

  return (
    <Helmet
      htmlAttributes={{
        lang
      }}
      defaultTitle={defaultTitle}
      title={title}
      titleTemplate={`%s / ${defaultTitle}`}
      meta={[
        {
          name: `viewport`,
          content: `width=device-width,initial-scale=1`
        },
        {
          name: `description`,
          content: metaDescription
        },
        {
          property: `og:title`,
          content: metaTitle
        },
        {
          property: `og:description`,
          content: metaDescription
        },
        {
          property: `og:type`,
          contentType: type
        },
        ...typeMeta,
        {
          property: `og:image`,
          content: metaPoster
        },
        {
          property: `og:image:secure_url`,
          content: metaPoster
        },
        ...imageMeta,
        {
          property: `og:url`,
          content: linkCanonical
        },
        {
          property: `og:site_name`,
          content: defaultTitle
        },
        {
          property: `og:locale`,
          contentType: locale
        },

        {
          name: `twitter:card`,
          content: `summary_large_image`
        },
        {
          name: `twitter:site`,
          content: site.siteMetadata.twitterHandle
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.twitterHandle
        },
        {
          name: `twitter:title`,
          content: metaTitle
        },
        {
          name: `twitter:image`,
          content: metaPoster
        },
        {
          name: `twitter:description`,
          content: metaDescription
        }
      ].concat(meta)}>
      <link rel="canonical" href={ensureTrailingSlash(linkCanonical)} />
      <link rel="shortcut icon" type="image/png" href={defaultFavicon} />

      <link
        rel="sitemap"
        type="application/xml"
        href={`${site.siteMetadata.siteUrl}/sitemap.xml`}
      />
    </Helmet>
  );
};

export default memo(Head);
